// src/components/Control/robot/drive.ts
// Ручное движение — один протокол для клавиатуры, кнопок и джойстика:
//   пока источник «держит» движение, {"linear", "angular", "source"} уходит в
//   robot/<id>/cmd_vel каждые REPEAT_MS (робот держит команду ~2 с);
//   отпустили — {"cmd": "stop", "source"}.
// Глобальный STOP: {"cmd": "emergency_stop"} в cmd_vel (fleet_adapter заодно
// переводит автономию в idle), "idle" в robot/<id>/mode и отмена поездки
// {"cmd": "cancel"} в robot/<id>/goal. Подтверждения не требует.
// Скорость общая для всех источников (м/с), хранится в браузере: kbd.speed.
import { useEffect } from 'react';
import * as zenoh from '@eclipse-zenoh/zenoh-ts';
import { Store } from './store';
import { putJson } from '../../../hooks/useZenohSubscription';
import { loadPref, savePref } from '../../../types/features';

export const REPEAT_MS = 250;
export const SPEED_MIN = 0.05;
export const SPEED_MAX = 0.5;
export const SPEED_STEP = 0.05;
// Поворот: угловая скорость пропорциональна выбранной (0,2 м/с -> 0,6 рад/с)
export const TURN_PER_SPEED = 3;

export type Move = 'forward' | 'backward' | 'left' | 'right';

export interface DriveState {
  speed: number;
  keyboard: boolean;          // управление с клавиатуры включено
  moving: boolean;            // какой-то источник сейчас держит движение
  held: Move[];               // нажатые направления (для подсветки)
  lastSent: { text: string; ok: boolean; at: number } | null;
  stoppedAt: number;          // последний STOP (для короткого подтверждения)
  lastCommand: string;
}

export class Drive {
  readonly store = new Store<DriveState>({
    speed: loadPref('kbd.speed', 0.2),
    keyboard: loadPref('kbd.enabled', true),
    moving: false,
    held: [],
    lastSent: null,
    stoppedAt: 0,
    lastCommand: '-',
  });

  private session: zenoh.Session | null = null;
  private robotId = '';
  private sources = new Map<string, { v: number; w: number; at: number }>();
  private timer: ReturnType<typeof setInterval> | null = null;

  setTarget(session: zenoh.Session | null, robotId: string) {
    if (robotId !== this.robotId && this.sources.size) this.releaseAll();
    this.session = session;
    this.robotId = robotId;
  }

  private get prefix() {
    return `robot/${this.robotId}`;
  }

  private send(msg: Record<string, unknown>) {
    const text = 'cmd' in msg ? String(msg.cmd) : `v=${msg.linear} м/с, w=${msg.angular} рад/с`;
    this.store.set({ lastCommand: text });
    if (!this.session) {
      this.store.set({ lastSent: { text: 'не отправлено: нет подключения к Zenoh', ok: false, at: Date.now() } });
      return;
    }
    putJson(this.session, `${this.prefix}/cmd_vel`, msg).then(ok => {
      this.store.set({ lastSent: { text: ok ? `→ ${this.prefix}/cmd_vel: ${text}` : `ошибка отправки: ${text}`, ok, at: Date.now() } });
    });
  }

  private tick = () => {
    // Последний изменённый источник управляет движением
    let best: [string, { v: number; w: number; at: number }] | null = null;
    for (const entry of Array.from(this.sources.entries())) {
      if (!best || entry[1].at > best[1].at) best = entry;
    }
    if (!best) return;
    const [src, { v, w }] = best;
    if (Math.abs(v) < 1e-3 && Math.abs(w) < 1e-3) return;
    this.send({ linear: Number(v.toFixed(3)), angular: Number(w.toFixed(3)), source: src });
  };

  // Источник держит движение (v м/с, w рад/с); повторный вызов обновляет вектор
  hold(source: string, v: number, w: number) {
    const fresh = !this.sources.has(source);
    this.sources.set(source, { v, w, at: Date.now() });
    if (fresh || !this.timer) this.tick();
    if (!this.timer) this.timer = setInterval(this.tick, REPEAT_MS);
    if (!this.store.get().moving) this.store.set({ moving: true });
  }

  release(source: string) {
    if (!this.sources.delete(source)) return;
    if (this.sources.size === 0) {
      if (this.timer) clearInterval(this.timer);
      this.timer = null;
      this.store.set({ moving: false });
      this.send({ cmd: 'stop', source });
    } else {
      this.tick();
    }
  }

  releaseAll() {
    const any = this.sources.size > 0;
    this.sources.clear();
    if (this.timer) clearInterval(this.timer);
    this.timer = null;
    this.store.set({ moving: false, held: [] });
    if (any) this.send({ cmd: 'stop', source: 'site' });
  }

  // Направление кнопкой/клавишей с текущей скоростью
  vector(moves: Iterable<Move>): { v: number; w: number } {
    const set = new Set(moves);
    const s = this.store.get().speed;
    const v = (set.has('forward') ? s : 0) - (set.has('backward') ? s : 0);
    const w = ((set.has('left') ? 1 : 0) - (set.has('right') ? 1 : 0)) * s * TURN_PER_SPEED;
    return { v, w };
  }

  emergencyStop(cancelGoal = true) {
    this.sources.clear();
    if (this.timer) clearInterval(this.timer);
    this.timer = null;
    this.store.set({ moving: false, held: [], stoppedAt: Date.now() });
    this.send({ cmd: 'emergency_stop', timestamp: Date.now(), source: 'site' });
    if (this.session) {
      this.session.put(`${this.prefix}/mode`, 'idle').catch(() => {});
      if (cancelGoal) putJson(this.session, `${this.prefix}/goal`, { cmd: 'cancel' });
    }
  }

  setSpeed(next: number) {
    const v = Math.round(Math.min(SPEED_MAX, Math.max(SPEED_MIN, next)) * 100) / 100;
    savePref('kbd.speed', v);
    this.store.set({ speed: v });
    return v;
  }

  setKeyboard(on: boolean) {
    savePref('kbd.enabled', on);
    this.store.set({ keyboard: on });
    if (!on) this.release('keyboard');
  }

  dispose() {
    this.releaseAll();
  }
}

const MOVE_KEYS: Record<string, Move> = {
  ArrowUp: 'forward', KeyW: 'forward',
  ArrowDown: 'backward', KeyS: 'backward',
  ArrowLeft: 'left', KeyA: 'left',
  ArrowRight: 'right', KeyD: 'right',
};
const FASTER = ['Equal', 'NumpadAdd'];
const SLOWER = ['Minus', 'NumpadSubtract'];
const STOP = ['Space', 'Escape'];
const MAP = ['KeyM'];

function isTyping(target: EventTarget | null): boolean {
  const el = target as HTMLElement | null;
  if (!el) return false;
  const tag = el.tagName;
  return tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT' || el.isContentEditable;
}

// Клавиатура: ↑/W ↓/S ←/A →/D (можно вместе — дугой), +/− скорость, M — рассказать,
// что на карте, Пробел/Esc — STOP. Клавиши по физическому положению (e.code),
// поэтому работают и в русской раскладке; в полях ввода не перехватываются.
// Окно потеряло фокус или вкладка скрыта — стоп. Работает при свёрнутом управлении.
export function useKeyboardDrive(
  drive: Drive,
  enabled: boolean,
  hooks: { onFlash: (text: string) => void; onDescribeMap: () => void; onStop: () => void },
) {
  useEffect(() => {
    if (!enabled) return;
    const held = new Set<Move>();
    const update = () => {
      drive.store.set({ held: Array.from(held) });
      if (held.size) {
        const { v, w } = drive.vector(held);
        drive.hold('keyboard', v, w);
      } else {
        drive.release('keyboard');
      }
    };
    const onDown = (e: KeyboardEvent) => {
      if (e.ctrlKey || e.altKey || e.metaKey || isTyping(e.target)) return;
      const move = MOVE_KEYS[e.code];
      if (move) {
        e.preventDefault(); // стрелки не прокручивают страницу
        if (e.repeat || held.has(move)) return;
        held.add(move);
        update();
      } else if (STOP.includes(e.code)) {
        // Пробел на кнопке не должен её «нажимать» повторно
        e.preventDefault();
        held.clear();
        drive.store.set({ held: [] });
        hooks.onStop();
      } else if (FASTER.includes(e.code) || SLOWER.includes(e.code)) {
        e.preventDefault();
        const next = drive.setSpeed(drive.store.get().speed + (FASTER.includes(e.code) ? SPEED_STEP : -SPEED_STEP));
        if (held.size) update();
        hooks.onFlash(`Скорость ${next.toFixed(2)} м/с`);
      } else if (MAP.includes(e.code) && !e.repeat) {
        e.preventDefault();
        hooks.onDescribeMap();
        hooks.onFlash('Рассказываю, что на карте');
      }
    };
    const onUp = (e: KeyboardEvent) => {
      const move = MOVE_KEYS[e.code];
      if (move && held.delete(move)) update();
    };
    const stopAll = () => {
      if (!held.size) return;
      held.clear();
      update();
    };
    const onVisibility = () => document.hidden && stopAll();
    window.addEventListener('keydown', onDown);
    window.addEventListener('keyup', onUp);
    window.addEventListener('blur', stopAll);
    document.addEventListener('visibilitychange', onVisibility);
    return () => {
      window.removeEventListener('keydown', onDown);
      window.removeEventListener('keyup', onUp);
      window.removeEventListener('blur', stopAll);
      document.removeEventListener('visibilitychange', onVisibility);
      stopAll();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [drive, enabled]);
}
